'use client';

import { useIsomorphicEffect } from '@ocodelib/mui/hooks';
import { createContext, useEffect, useMemo, useRef } from 'react';
import { PyBasicAppManager } from './PyBasicAppManager';
import type { IAppConfig, IPyBasicLayoutConfig, PyBasicAppType } from './types';
import { usePyBasicAppLoader } from './usePyBasicAppLoader';

export interface IPyBasicAppContext {
  appManager: PyBasicAppManager;
  app: PyBasicAppType;
}

export const PyBasicAppContext = createContext<IPyBasicAppContext | null>(null);

interface Props {
  layoutConfig: IPyBasicLayoutConfig;
  appConfig: IAppConfig;
  onLoadApp?: (app: PyBasicAppType | null) => void;
  children: React.ReactNode;
}

export function PyBasicAppProvider(props: Props) {
  const { children, layoutConfig } = props;
  const appConfigRef = useRef(props.appConfig);
  appConfigRef.current = props.appConfig;
  const onLoadAppRef = useRef(props.onLoadApp);
  onLoadAppRef.current = props.onLoadApp;

  const appManager = useMemo(() => new PyBasicAppManager(appConfigRef.current), []);
  const { app } = usePyBasicAppLoader({
    previewMode: props.appConfig.previewMode ?? false,
    newProjectLoader: props.appConfig.newProjectLoader,
    initialProjectLoader: props.appConfig.initialProjectLoader,
  });

  useIsomorphicEffect(() => {
    appManager.setup(layoutConfig ?? null);
  }, [appManager, layoutConfig]);

  useIsomorphicEffect(() => {
    appManager.setApp(app ?? null);
  }, [appManager, app]);

  const contextData: IPyBasicAppContext | null = useMemo(
    () =>
      app
        ? {
            appManager,
            app,
          }
        : null,
    [appManager, app],
  );

  useEffect(() => {
    const onLoadApp = onLoadAppRef.current;
    if (!onLoadApp) return;
    onLoadApp(app ?? null);
    return () => {
      onLoadApp(null);
    };
  }, [app]);

  if (!contextData) return null;

  return <PyBasicAppContext.Provider value={contextData}>{children}</PyBasicAppContext.Provider>;
}
